import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsFocused } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { logAddPaymentMethodClick } from 'features/payment/utils/analytics';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Icon } from 'ucl/components/icon';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import AccountLayout from 'components/layout/drawer-page';
import LoadingAnimation from 'components/loading-animation';
import { Box, Typography } from 'components/ui';
import RadioIndicator from 'components/ui/radio-indicator';
import { useModalFocusTrap } from 'hooks/accessibility/use-modal-focus-trap';
import useDialogModal from 'hooks/use-dialog-modal';
import { emptyCartContext, useCartContext } from 'state/cart';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getDesignToken, primitive, spacing } from 'styles/constants';
import { fontSizes } from 'styles/constants/fonts';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { routes } from 'utils/routing';

import { usePaymentContext } from '../state';
import { CardTypes, IPaymentMethod } from '../types';
import { mapPaymentMethodCardTypes, shouldReVaultPaymentMethod } from '../utils';

import { PaymentMethodDetails } from './payment-method-details';
import { PaymentMethodIcon } from './payment-method-icon';

type AccountPaymentMethodItemProps = {
  isDigitalGiftCardNumberExposedEnabled: boolean;
  isDefault: boolean;
  isOrbitalPaymentEnabled: boolean;
  method: IPaymentMethod;
  onDelete: (accountIdentifier: string) => Promise<void>;
  onNavigateToChildPaymentModal: () => void;
  onSelect: (accountIdentifier: string) => void;
};

const getAccountIdentifier = (method: IPaymentMethod) =>
  method.accountIdentifier ?? method.fdAccountId ?? '';

const isRemovableAccountMethod = (method: IPaymentMethod) =>
  !!method.credit &&
  method.credit.cardType !== CardTypes.APPLE_PAY &&
  method.credit.cardType !== CardTypes.GOOGLE_PAY;

const getPaymentMethodAccessibilityLabel = (
  method: IPaymentMethod,
  isDigitalGiftCardNumberExposedEnabled: boolean
): string => {
  if (method.credit) {
    if (method.credit.cardType === CardTypes.APPLE_PAY) {
      return t('applePay');
    }
    if (method.credit.cardType === CardTypes.GOOGLE_PAY) {
      return t('googlePay');
    }

    const cardType = mapPaymentMethodCardTypes(method.credit.cardType) || t('card');

    return method.credit.alias
      ? t('paymentXEndingInY', { cardType, ccLast4: method.credit.alias })
      : cardType;
  }

  if (method.prepaid) {
    if (!isDigitalGiftCardNumberExposedEnabled) {
      return t('giftCard');
    }

    const lastFour = method.prepaid.cardNumber?.slice(-4);

    return lastFour
      ? t('paymentXEndingInY', { cardType: t('giftCard'), ccLast4: lastFour })
      : t('giftCard');
  }

  if (method.paypal) {
    return t('paypal');
  }
  if (method.venmo) {
    return t('venmo');
  }
  if (method.cashapp) {
    return t('cashAppPay');
  }
  if (method.cash) {
    return t('cash');
  }

  return t('card');
};

function AccountPaymentMethodItem({
  isDigitalGiftCardNumberExposedEnabled,
  isDefault,
  isOrbitalPaymentEnabled,
  method,
  onDelete,
  onNavigateToChildPaymentModal,
  onSelect,
}: AccountPaymentMethodItemProps) {
  const router = useRouter();
  const isInCart = useCartContext() !== emptyCartContext;
  const accountIdentifier = getAccountIdentifier(method);
  const { isExpired = false, isInvalid = false } = shouldReVaultPaymentMethod(
    method,
    isOrbitalPaymentEnabled
  );
  const requiresUpdate = isExpired || isInvalid;
  const isRemovable = isRemovableAccountMethod(method);
  const paymentMethodLabel = getPaymentMethodAccessibilityLabel(
    method,
    isDigitalGiftCardNumberExposedEnabled
  );
  const paymentMethodStatusLabel = requiresUpdate
    ? isExpired
      ? t('expired')
      : t('paymentMethodNotAvailable')
    : undefined;
  const paymentMethodAccessibilityLabel = [paymentMethodLabel, paymentMethodStatusLabel]
    .filter(Boolean)
    .join(', ');

  const handleSelect = useCallback(() => {
    if (requiresUpdate) {
      onNavigateToChildPaymentModal();
      router.navigate(
        `${routes.addCard}?accountToDelete=${accountIdentifier}&isInCart=${isInCart}`
      );
      return;
    }

    onSelect(accountIdentifier);
  }, [
    accountIdentifier,
    isInCart,
    onNavigateToChildPaymentModal,
    onSelect,
    requiresUpdate,
    router,
  ]);

  const [DeletionConfirmationDialog, openDeletionConfirmationDialog] = useDialogModal({
    onConfirm: () => onDelete(accountIdentifier),
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Delete payment method',
  });

  return (
    <>
      <View style={styles.methodRow} testID={`account-payment-method-${accountIdentifier}`}>
        <Pressable
          accessible
          accessibilityChecked={requiresUpdate ? undefined : isDefault}
          accessibilityLabel={paymentMethodAccessibilityLabel}
          accessibilityRole={requiresUpdate ? 'button' : 'radio'}
          accessibilityState={requiresUpdate ? undefined : { checked: isDefault }}
          onPress={handleSelect}
          style={styles.methodSelection}
          testID={`select-account-payment-method-${accountIdentifier}`}
        >
          <PaymentMethodIcon bordered paymentMethod={method} size="md" />
          <View
            style={styles.methodDetails}
            testID={`account-payment-method-details-container-${accountIdentifier}`}
          >
            <DatadogSessionReplayMask
              testID={`account-payment-method-details-${accountIdentifier}`}
            >
              <PaymentMethodDetails paymentMethod={method} displayFor="selectorOption" />
              {requiresUpdate && (
                <Typography style={styles.unavailableText} variant="copyTwo">
                  {isExpired ? t('expired') : t('paymentMethodNotAvailable')}
                </Typography>
              )}
            </DatadogSessionReplayMask>
          </View>
          <RadioIndicator
            {...hiddenAccessibilityPlatformProps}
            isDisabled={requiresUpdate}
            isSelected={isDefault}
            testID={`account-payment-method-radio-indicator-${accountIdentifier}`}
          />
        </Pressable>
        {isRemovable && (
          <Pressable
            accessibilityLabel={t('removePaymentMethodAccessibilityLabel', {
              defaultValue: 'Remove {paymentMethod} payment method',
              paymentMethod: paymentMethodLabel,
            })}
            accessibilityRole="button"
            hitSlop={spacing.$2}
            onPress={openDeletionConfirmationDialog}
            style={styles.removeButton}
            testID={`remove-account-payment-method-${accountIdentifier}`}
          >
            <Icon size="$7" variant="remove" {...hiddenAccessibilityPlatformProps} />
          </Pressable>
        )}
      </View>
      <View style={styles.divider} />
      <DeletionConfirmationDialog
        testID="delete-confirmation-dialog"
        heading={t('deletePaymentMethod')}
        body={t('confirmDeletePaymentMethod')}
        modalAppearanceEventMessage="Confirmation: Delete Payment Method"
      />
    </>
  );
}

export function AccountPaymentMethodList() {
  const isFocused = useIsFocused();
  const router = useRouter();
  const isClosingRef = useRef(false);
  const preserveFocusTriggerRef = useRef(false);
  const suppressFocusRestorationRef = useRef(false);
  const handleNavigateToChildPaymentModal = useCallback(() => {
    preserveFocusTriggerRef.current = true;
    suppressFocusRestorationRef.current = true;
  }, []);
  const { modalRef, getModalA11yProps } = useModalFocusTrap({
    active: isFocused,
    label: t('paymentMethods'),
    isClosingRef,
    preserveFocusTriggerRef,
    suppressFocusRestorationRef,
  });
  const {
    defaultPaymentMethodId,
    deletePaymentMethod,
    getPrepaidPaymentMethod,
    isOrbital: isOrbitalPaymentEnabled,
    loading,
    paymentMethods,
    setDefaultPaymentMethodId,
  } = usePaymentContext();
  const [selectedMethodId, setSelectedMethodId] = useState(defaultPaymentMethodId);
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const isDigitalGiftCardNumberExposedEnabled = useFlag(
    LaunchDarklyFlag.IS_DIGITAL_GIFT_CARD_NUMBER_EXPOSED_ENABLED
  );
  const isInCart = useCartContext() !== emptyCartContext;
  const hasGiftCard = !!getPrepaidPaymentMethod();

  useEffect(() => {
    setSelectedMethodId(defaultPaymentMethodId);
  }, [defaultPaymentMethodId]);

  const handleSelect = useCallback(
    (accountIdentifier: string) => {
      setSelectedMethodId(accountIdentifier);
      setDefaultPaymentMethodId(accountIdentifier);
    },
    [setDefaultPaymentMethodId]
  );

  const giftCardButtonText = hasGiftCard
    ? t('transferBalanceFromPrepaidCard', { cardAlias: t('cardAlias.bk') })
    : t('addNewPaymentMethodGiftCard');
  const giftCardRoute = hasGiftCard ? routes.transferGiftCard : routes.addGiftCard;
  const selectedAccountIdentifier = selectedMethodId || defaultPaymentMethodId;

  return (
    <Box
      ref={modalRef}
      style={styles.modalFocusTrapWrapper}
      testID="payment-methods-modal"
      {...getModalA11yProps({ nativeRole: 'none' })}
    >
      <AccountLayout
        headerText={t('paymentMethods')}
        footer={
          <>
            <ActionButton
              mb="$4"
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
              onPress={() => {
                handleNavigateToChildPaymentModal();
                router.navigate(`${routes.addCard}?isInCart=${isInCart}`);
              }}
              testID="add-new-payment-method"
            >
              {t('addPaymentMethodOrReload')}
            </ActionButton>
            {enableGiftCard && (
              <ActionButton
                fullWidth
                variant={ActionButtonVariants.OUTLINE}
                onPress={() => {
                  handleNavigateToChildPaymentModal();
                  logAddPaymentMethodClick();
                }}
                testID={hasGiftCard ? 'transfer-gift-card' : 'add-new-gift-card'}
                to={giftCardRoute}
              >
                {giftCardButtonText}
              </ActionButton>
            )}
          </>
        }
      >
        {loading ? (
          <LoadingAnimation />
        ) : paymentMethods.length === 0 ? (
          <Typography style={styles.emptyText}>{t('noPaymentMethodOnFile')}</Typography>
        ) : (
          <>
            <View style={styles.defaultTextWrapper}>
              <Typography
                accessible
                accessibilityLevel={2}
                accessibilityRole="header"
                style={styles.defaultText}
                variant="copyOne"
              >
                {t('accountDefault')}
              </Typography>
            </View>
            <ScrollView
              accessibilityLabel={t('paymentMethods')}
              accessibilityRole="radiogroup"
              style={styles.methodsList}
              testID="account-saved-payments"
            >
              {paymentMethods.map(method => {
                const accountIdentifier = getAccountIdentifier(method);

                return (
                  <AccountPaymentMethodItem
                    key={accountIdentifier}
                    isDigitalGiftCardNumberExposedEnabled={isDigitalGiftCardNumberExposedEnabled}
                    isDefault={accountIdentifier === selectedAccountIdentifier}
                    isOrbitalPaymentEnabled={isOrbitalPaymentEnabled}
                    method={method}
                    onDelete={deletePaymentMethod}
                    onNavigateToChildPaymentModal={handleNavigateToChildPaymentModal}
                    onSelect={handleSelect}
                  />
                );
              })}
            </ScrollView>
          </>
        )}
      </AccountLayout>
    </Box>
  );
}

const styles = StyleSheet.create({
  defaultTextWrapper: {
    width: '100%',
    height: 40,
    justifyContent: 'center',
  },
  modalFocusTrapWrapper: {
    flex: 1,
  },
  defaultText: {
    textTransform: 'uppercase',
    fontSize: fontSizes.sm,
    marginRight: 10,
    textAlign: 'right',
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: primitive.$blackOpacity10,
  },
  emptyText: {
    textAlign: 'center',
  },
  methodDetails: {
    alignItems: 'flex-start',
    flex: 1,
    minWidth: 0,
  },
  methodRow: {
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: spacing.$13_5,
    paddingVertical: spacing.$2,
    width: '100%',
  },
  methodsList: {
    width: '100%',
  },
  methodSelection: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: spacing.$2,
    padding: spacing.$1,
  },
  removeButton: {
    alignItems: 'center',
    height: 40,
    justifyContent: 'center',
    marginLeft: spacing.$2,
    width: 40,
  },
  unavailableText: {
    color: getDesignToken('text-error'),
  },
});
